import React, { useReducer, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

interface BackdropModalProps {
  visible: boolean;
  children: React.ReactNode;
  closeModal: () => void;
}

const backdropElement = document.createElement('div');

const BackdropModal = ({ visible, closeModal, children }: BackdropModalProps) => {
  const { current } = useRef(backdropElement);
  useEffect(() => {
    const { body } = document;
    body.appendChild(current);
    return () => {
      body.removeChild(current);
    };
  }, [current]);

  const [enableAnimation, setupAnimation] = useReducer(() => true, false);
  useEffect(() => {
    if (!enableAnimation && visible) {
      setupAnimation();
    }
  }, [enableAnimation, visible]);

  return ReactDOM.createPortal(
    <OverflowController visible={visible}>
      <ModalArea visible={visible} enableAnimation={enableAnimation}>
        {children}
        <Backdrop onClick={closeModal} />
      </ModalArea>
    </OverflowController>,
    current,
  );
};
interface BackdropModalContainerProps {
  visible: boolean;
  children: React.ReactNode;
}
const OverflowController = ({ visible, children }: BackdropModalContainerProps) => {
  useEffect(() => {
    document.body.style.overflowY = visible ? 'hidden' : 'auto';

    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [visible]);
  return <>{children}</>;
};

const Backdrop = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: -1;
`;
interface ModalAreaProps {
  visible: boolean;
  enableAnimation: boolean;
}
const ModalArea = styled.div<ModalAreaProps>`
  position: fixed;
  animation: 0.3s ${(props) => (props.visible ? 'show' : 'disappear')};
  animation-fill-mode: forwards;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 9999;
  overflow: auto;
  opacity: 0;
  visibility: hidden;

  @keyframes show {
    0% {
      opacity: 0;
      visibility: hidden;
    }
    100% {
      opacity: 1;
      visibility: visible;
    }
  }

  @keyframes disappear {
    0% {
      opacity: 1;
      visibility: ${(props) => (props.enableAnimation ? 'visible' : 'hidden')};
    }
    100% {
      visibility: hidden;
      opacity: 0;
    }
  }
`;

export default BackdropModal;
